<template>
  <nav class="glass-nav fixed top-0 w-full z-40">
    <div class="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
      <router-link :to="{ name: 'home' }" class="text-2xl font-black gradient-text">
        AUTHENTIQUE ARTIFICE
      </router-link>

      <div class="hidden md:flex flex-col items-start gap-2 py-2">
        <!-- Two Lines Categories -->
        <div class="flex gap-6 items-center font-bold">
          <router-link
            v-for="(cat, index) in categories.slice(0, Math.ceil(categories.length / 2))"
            :key="cat.id"
            :to="{ name: 'category', params: { id: cat.id } }"
            :class="[
              'transition-colors text-[11px] font-bold uppercase tracking-[0.15em] whitespace-nowrap',
              index % 2 === 0 ? 'text-fire-gold hover:text-red-500' : 'text-white/70 hover:text-red-500'
            ]"
          >
            {{ cat.name }}
          </router-link>
          
          <router-link
            to="/conseils-expert"
            class="text-fire-gold hover:text-red-500 transition-colors text-[11px] font-black uppercase tracking-[0.2em] border-l border-white/10 pl-6 whitespace-nowrap"
          >
            Conseils
          </router-link>
        </div>

        <div class="flex gap-6 items-center font-bold">
          <router-link
            v-for="(cat, index) in categories.slice(Math.ceil(categories.length / 2))"
            :key="cat.id"
            :to="{ name: 'category', params: { id: cat.id } }"
            :class="[
              'transition-colors text-[11px] font-bold uppercase tracking-[0.15em] whitespace-nowrap',
              (index + Math.ceil(categories.length / 2)) % 2 === 0 ? 'text-fire-gold hover:text-red-500' : 'text-white/70 hover:text-red-500'
            ]"
          >
            {{ cat.name }}
          </router-link>
        </div>
      </div>

      <!-- Cart & Auth (Desktop only) -->
      <div class="hidden md:flex items-center gap-4">
        
        <!-- Login Button (if NOT logged in) -->
        <button 
          v-if="!auth.isAuthenticated"
          @click="auth.showLoginModal = true"
          class="text-white/70 hover:text-fire-gold font-bold text-sm uppercase tracking-widest transition-colors mr-2"
        >
          Se connecter
        </button>

        <!-- User Dropdown (if logged in) -->
        <div v-if="auth.isAuthenticated" class="relative group">
          <button 
            @click="isUserMenuOpen = !isUserMenuOpen"
            class="flex items-center gap-2 p-1.5 rounded-2xl hover:bg-white/5 transition-all"
          >
            <div class="w-9 h-9 rounded-xl bg-fire-gold/10 flex items-center justify-center border border-fire-gold/20">
              <UserIcon class="w-5 h-5 text-fire-gold" />
            </div>
            <div class="hidden lg:block text-left mr-2">
              <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Compte</p>
              <p class="text-xs font-black text-white uppercase tracking-tighter truncate max-w-[100px]">{{ auth.user?.name }}</p>
            </div>
            <ChevronDownIcon :class="['w-4 h-4 text-gray-400 transition-transform', isUserMenuOpen ? 'rotate-180' : '']" />
          </button>

          <!-- Dropdown Menu -->
          <transition name="slide-up">
            <div 
              v-if="isUserMenuOpen" 
              class="absolute right-0 mt-3 w-64 rounded-3xl bg-[#0A0A0A] border border-white/10 shadow-2xl p-3 z-[60] backdrop-blur-xl"
            >
              <div class="px-4 py-3 mb-2 border-b border-white/5">
                <p class="text-[10px] font-black text-fire-gold uppercase tracking-[0.2em] mb-1">Connecté en tant que</p>
                <p class="text-sm font-bold text-white truncate">{{ auth.user?.email }}</p>
              </div>

              <router-link v-if="auth.isAdmin" to="/admin" class="dropdown-item text-fire-gold" @click="isUserMenuOpen = false">
                <div class="w-8 h-8 rounded-lg bg-fire-gold/10 flex items-center justify-center">
                  <ShieldCheckIcon class="w-4 h-4" />
                </div>
                Administration
              </router-link>

              <router-link to="/mes-commandes" class="dropdown-item" @click="isUserMenuOpen = false">
                <div class="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <ShoppingBagIcon class="w-4 h-4" />
                </div>
                Mes Commandes
              </router-link>

              <router-link to="/checkout" class="dropdown-item" @click="isUserMenuOpen = false">
                <div class="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <MapPinIcon class="w-4 h-4" />
                </div>
                Mes Adresses
              </router-link>

              <div class="h-px bg-white/5 my-2 mx-2"></div>

              <button @click="handleLogout" class="dropdown-item text-red-400 hover:bg-red-500/10 w-full text-left">
                <div class="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <ArrowRightOnRectangleIcon class="w-4 h-4 text-red-500" />
                </div>
                Déconnexion
              </button>
            </div>
          </transition>
        </div>

        <button
          @click="cart.openCart()"
          class="btn-primary p-2 px-4 py-2.5 flex items-center gap-2 relative rounded-2xl"
          id="cart-button"
        >
          <ShoppingCartIcon class="w-5 h-5" />
          <span class="hidden sm:inline text-xs font-black uppercase tracking-widest">Panier</span>
          <transition name="pop">
            <span
              v-if="cart.itemCount > 0"
              class="absolute -top-1 -right-1 bg-white text-black text-[10px] font-black rounded-lg min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-lg"
            >
              {{ cart.itemCount }}
            </span>
          </transition>
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import api from '../services/api';
import { 
  ShoppingCartIcon, 
  UserIcon, 
  ChevronDownIcon, 
  ShoppingBagIcon, 
  MapPinIcon, 
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  FireIcon,
  CloudIcon,
  SunIcon,
  HeartIcon,
  UsersIcon,
  BoltIcon,
  GiftIcon
} from '@heroicons/vue/24/solid';
import { computed } from 'vue';
import { useCartStore } from '../stores/cart';
import { useAuthStore } from '../stores/auth';

import { useCategoryStore } from '../stores/categories';

const categoryStore = useCategoryStore();
const categories = computed(() => categoryStore.categories);
const cart = useCartStore();
const auth = useAuthStore();
const router = useRouter();

const getIcon = (iconName) => {
  const icons = {
    SparklesIcon,
    FireIcon,
    CloudIcon,
    SunIcon,
    HeartIcon,
    UsersIcon,
    BoltIcon,
    GiftIcon
  };
  return icons[iconName] || SparklesIcon;
};

const isUserMenuOpen = ref(false);
const isCategoryMenuOpen = ref(false);

const handleLogout = () => {
  auth.logout();
  isUserMenuOpen.value = false;
  router.push('/');
};

onMounted(() => {
  categoryStore.fetchCategories();
});
</script>

<style scoped>
.dropdown-item {
  @apply flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-bold text-gray-400 hover:text-white hover:bg-white/5 transition-all cursor-pointer;
}

.slide-up-enter-active, .slide-up-leave-active { transition: all 0.3s cubic-bezier(0.19, 1, 0.22, 1); }
.slide-up-enter-from, .slide-up-leave-to { opacity: 0; transform: translateY(10px); }

.pop-enter-active { animation: pop 0.3s cubic-bezier(0.19, 1, 0.22, 1); }
.pop-leave-active { animation: pop 0.2s reverse; }
@keyframes pop {
  0% { transform: scale(0); }
  70% { transform: scale(1.3); }
  100% { transform: scale(1); }
}
</style>
